import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { ThemeContext } from "../../../constant/theme";
import { LanguageContext } from "../../../constant/LanguageContext";
import { translations_admin } from "../../../constant/Admin_Translations";
import { getNavItems } from "../config/userProfileConfig";

export const useUserProfileState = () => {
  // ============ HOOKS AND STATE ============
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { theme, setTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  const isRTL = language === 'ar';
  const t = translations_admin[language];

  // State management
  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);

  // This page (/userprofileadmin/*) is an admin's own employee profile — it
  // always renders the same "employee profile" content no matter what
  // viewType used to hold, so there is nothing to toggle in place here.
  // "user" is simply the permanent, correct label for whatever is on screen
  // while this page is mounted (matches getCurrentView() in
  // notificationNavigation.js, which treats this whole route prefix as view
  // "user" unconditionally). The real role gated onto this route is always
  // "admin"; there is no legitimate "manager" view to switch to from here.
  const viewType = 'user';

  // حفظ الصفحة الحالية
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('adminUserProfilePage');
    return savedPage || 'professionalProfile';
  });

  useEffect(() => {
    localStorage.setItem('adminUserProfilePage', currentPage);
  }, [currentPage]);

  useEffect(() => {
    // Sync currentPage with URL path
    const navItems = getNavItems(t);
    const pathParts = routerLocation.pathname.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];

    if (lastPart) {
      const matchedNavItem = navItems.find(
        (item) => item.page.toLowerCase() === lastPart.toLowerCase()
      );

      if (matchedNavItem) {
        if (currentPage !== matchedNavItem.page) {
          setCurrentPage(matchedNavItem.page);
        }
      } else {
        // Fallback to professionalProfile if path is unhandled or root
        if (currentPage !== 'professionalProfile') {
          setCurrentPage('professionalProfile');
        }
      }
    } else {
      if (currentPage !== 'professionalProfile') {
        setCurrentPage('professionalProfile');
      }
    }
  }, [routerLocation.pathname, t, currentPage]);

  // ============ EVENT HANDLERS ============
  const handleViewTypeChange = (type) => {
    // "user" is already the (only) view this page shows — nothing to do.
    // Only "admin" is reachable here (real role is always "admin" on this
    // route; there is no "manager" button to click). Persist the choice for
    // the destination shell before leaving.
    if (type === 'admin') {
      localStorage.setItem('viewType', 'admin');
      navigate('/dashboard/dashboard');
    }
  };

  const handleLogout = () => {
    try {
      logout();
      try {
        localStorage.removeItem('nav_admin');
        localStorage.removeItem('nav_manager');
        localStorage.removeItem('nav_employee');
      } catch (e) { }
      navigate("/login");
    } catch (error) {
      console.error(t.logoutError, error);
      navigate("/login");
    }
  };

  const handleSwitchManager = () => {
    try {
      localStorage.removeItem('adminUserProfilePage');
      navigate("/dashboard/dashboard");
    } catch (error) {
      console.error(t.switchError, error);
      navigate("/userprofileadmin");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    navigate(`/userprofileadmin/${page}`);
  };

  const toggleTheme = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    if (setTheme) {
      setTheme(newDarkMode ? "dark" : "light");
    }
  };

  return {
    user,
    profile,
    theme,
    setTheme,
    language,
    isRTL,
    t,
    notifications,
    setNotifications,
    sidebarOpen,
    setSidebarOpen,
    isDarkMode,
    setIsDarkMode,
    viewType,
    currentPage,
    setCurrentPage,
    handleViewTypeChange,
    handleLogout,
    handleSwitchManager,
    handlePageChange,
    toggleTheme,
  };
};
